import React, { useState, useMemo } from "react";
import { Button, Dialog, DialogTitle, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { observer } from "mobx-react-lite";
import RefreshIcon from "@material-ui/icons/Refresh";
import { useQuery } from "react-apollo-hooks";
import gql from "graphql-tag";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import GetAppIcon from "@material-ui/icons/GetApp";

const NativeInvoiceTable = observer((props) => {
  const [state, setState] = useState({
    open: false,
    currentPage: 0,
    rowsPerPage: 5,
    selectedRows: [],
    orderBy: "vencimento",
    order: "asc",
    searchTerm: "",
    selectedFatura: null,
  });

  const toggleRowSelection = (index) => {
    const selectedIndex = state.selectedRows.indexOf(index);
    let newSelected = [...state.selectedRows];

    if (selectedIndex === -1) {
      newSelected.push(index);
    } else {
      newSelected.splice(selectedIndex, 1);
    }

    setState({ ...state, selectedRows: newSelected });
  };

  const selectAllRows = (event) => {
    if (event.target.checked) {
      const newSelectedIndexes = payments.map((_, index) => index);
      setState({ ...state, selectedRows: newSelectedIndexes });
      return;
    }
    setState({ ...state, selectedRows: [] });
  };

  const isSelected = (index) => state.selectedRows.indexOf(index) !== -1;

  const handleChangePage = (newPage, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setState({ ...state, currentPage: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    setState({
      ...state,
      rowsPerPage: parseInt(event.target.value, 10),
      currentPage: 0,
    });
  };

  const handleRequestSort = (property) => {
    const isAsc = state.orderBy === property && state.order === "asc";
    setState({
      ...state,
      order: isAsc ? "desc" : "asc",
      orderBy: property,
    });
  };

  const handleSearch = (event) => {
    setState({ ...state, searchTerm: event.target.value, currentPage: 0 });
  };

  const query = gql`
    query payment($plan: Int, $personId: Int) {
      payment(
        where: {
          _or: [
            { payment_plan: { _eq: $plan } }
            { person: { _eq: $personId } }
          ]
          deleted_at: { _is_null: true }
        }
      ) {
        id
        payment_id
        disabled
        payment_plan
        value
        status
        due_date
        description
        link
        org
        paid_at
        platform
      }
    }
  `;

  let plan = props.payment_plan;
  if (!plan) plan = 0;
  const { data, error, loading, refetch } = useQuery(query, {
    variables: { plan, personId: props.personId },
    fetchPolicy: "network-only",
  });

  const payments = useMemo(() => {
    let paymentsArray = [];

    if (loading) {
      return [{ msg: "Buscando no BD..." }];
    }

    if (error) {
      return [{ Erro: error.message }];
    }

    if (data && data.payment) {
      return data.payment.map((payment) => {
        var atraso = null;
        var today = new Date().toISOString().split("T")[0];
        var current_plan =
          payment.payment_plan && payment.payment_plan === props.payment_plan
            ? "SIM"
            : null;
        if (
          (payment.status === "pending" ||
            (payment.status === "expired" && !payment.disabled)) &&
          today > payment.due_date
        )
          atraso = "ATRASO";
        var valor = "R$ " + payment.value;

        return {
          id: payment.payment_id,
          vencimento: payment.due_date,
          valor,
          status: payment.status,
          atraso,
          descrição: payment.description,
          data_pagamento: payment.paid_at,
          link: payment.link,
          plano_atual: current_plan,
          org: payment.org,
          payment_id: payment.id,
          value: payment.value,
          platform: payment.platform,
        };
      });
    }

    return paymentsArray;
  }, [data, error, loading, props.payment_plan]);

  const columns = [
    { id: "id", label: "ID", sortable: true },
    { id: "vencimento", label: "Vencimento", sortable: true },
    { id: "valor", label: "Valor", sortable: true },
    { id: "status", label: "Status", sortable: true },
    { id: "atraso", label: "Atraso", sortable: true },
    { id: "descrição", label: "Descrição", sortable: true },
    { id: "data_pagamento", label: "Data Pagamento", sortable: true },
    { id: "link", label: "Link", sortable: false },
    { id: "plano_atual", label: "Plano Atual", sortable: false },
  ];

  const translateStatus = (status) => {
    switch (status) {
      case "paid":
        return "Pago";
      case "pending":
        return "Pendente";
      case "expired":
        return "Expirado";
      default:
        return status;
    }
  };

  const getStatusByTranslation = (translatedTerm) => {
    const lowerTerm = translatedTerm.toLowerCase().trim();
    if (lowerTerm === "pago") return "paid";
    if (lowerTerm === "pendente") return "pending";
    if (lowerTerm === "expirado") return "expired";
    return null;
  };

  const {
    filteredPayments,
    sortedPayments,
    paginatedPayments,
    totalPages,
    emptyRows,
    start,
  } = useMemo(() => {
    const filtered = payments.filter((payment) => {
      const searchLower = state.searchTerm.toLowerCase();

      const matchDescription =
        payment.descrição &&
        payment.descrição.toLowerCase().includes(searchLower);

      const translatedStatus =
        payment.status && translateStatus(payment.status).toLowerCase();
      const matchStatus =
        payment.status &&
        (payment.status.toLowerCase().includes(searchLower) ||
          (translatedStatus && translatedStatus.includes(searchLower)));

      const reversedStatus = getStatusByTranslation(searchLower);
      const matchReversedStatus =
        payment.status &&
        reversedStatus &&
        payment.status.toLowerCase() === reversedStatus;

      const matchId = payment.id && payment.id.toString().includes(searchLower);

      return matchDescription || matchStatus || matchReversedStatus || matchId;
    });

    const sorted = [...filtered].sort((a, b) => {
      const isAsc = state.order === "asc";
      if (!a[state.orderBy] && !b[state.orderBy]) return 0;
      if (!a[state.orderBy]) return 1;
      if (!b[state.orderBy]) return -1;

      if (
        state.orderBy === "vencimento" ||
        state.orderBy === "data_pagamento"
      ) {
        const dateA = a[state.orderBy]
          ? new Date(a[state.orderBy])
          : new Date(0);
        const dateB = b[state.orderBy]
          ? new Date(b[state.orderBy])
          : new Date(0);
        return isAsc ? dateA - dateB : dateB - dateA;
      } else if (state.orderBy === "valor") {
        const valA = a[state.orderBy]
          ? parseFloat(a[state.orderBy].replace("R$ ", ""))
          : 0;
        const valB = b[state.orderBy]
          ? parseFloat(b[state.orderBy].replace("R$ ", ""))
          : 0;
        return isAsc ? valA - valB : valB - valA;
      } else {
        return isAsc
          ? String(a[state.orderBy]).localeCompare(String(b[state.orderBy]))
          : String(b[state.orderBy]).localeCompare(String(a[state.orderBy]));
      }
    });

    const start = state.currentPage * state.rowsPerPage;
    const paginated = sorted.slice(start, start + state.rowsPerPage);
    const total = Math.ceil(sorted.length / state.rowsPerPage);
    const empty =
      state.rowsPerPage - Math.min(state.rowsPerPage, sorted.length - start);

    return {
      filteredPayments: filtered,
      sortedPayments: sorted,
      paginatedPayments: paginated,
      totalPages: total,
      emptyRows: empty,
      start,
    };
  }, [
    payments,
    state.searchTerm,
    state.orderBy,
    state.order,
    state.currentPage,
    state.rowsPerPage,
  ]);

  const { allSelected, someSelected } = useMemo(() => {
    return {
      allSelected:
        payments.length > 0 && state.selectedRows.length === payments.length,
      someSelected:
        state.selectedRows.length > 0 &&
        state.selectedRows.length < payments.length,
    };
  }, [payments.length, state.selectedRows.length]);

  const getStatusStyle = (status) => {
    let style = {
      borderRadius: "5px",
      padding: "5px",
      textAlign: "center",
      display: "inline-block",
      minWidth: "80px",
    };

    switch (status) {
      case "paid":
        style.background = "lightgreen";
        break;
      case "pending":
        style.background = "yellow";
        break;
      case "expired":
        style.background = "lightgray";
        break;
      default:
        break;
    }

    return style;
  };

  const openFaturaDetails = (fatura) => {
    if (!fatura || !fatura.id) return;
    setState({
      ...state,
      open: true,
      selectedFatura: { ...fatura },
    });
  };

  const handleRefresh = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      await refetch();
    } catch (error) {
      console.error("Erro ao atualizar dados:", error);
    }
  };

  const handleSelectedActions = async (action) => {
    try {
      const selectedItems = state.selectedRows.map((index) => {
        const item = sortedPayments[index];
        return { ...item };
      });

      if (action === "cancel") {
        if (window.confirm("Cancelar faturas selecionadas?")) {
          for (const fatura of selectedItems) {
            await props.hidePayment(fatura);
          }
        }
      } else if (action === "include") {
        for (const fatura of selectedItems) {
          await props.setPlan(fatura, props.payment_plan);
        }
      }

      setState({ ...state, selectedRows: [] });
      refetch();
    } catch (error) {
      console.error("Erro ao processar ação:", error);
      alert(
        "Erro ao processar a ação. Verifique o console para mais detalhes."
      );
    }
  };

  const exportToCSV = () => {
    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString("pt-BR");
    };

    const headers = [
      "ID",
      "Vencimento",
      "Valor",
      "Status",
      "Atraso",
      "Descrição",
      "Data Pagamento",
      "Link",
      "Plano Atual",
    ];

    const csvData = sortedPayments.map((payment) => [
      payment.id,
      formatDate(payment.vencimento),
      payment.valor,
      translateStatus(payment.status),
      payment.atraso || "",
      payment.descrição || "",
      formatDate(payment.data_pagamento),
      payment.link || "",
      payment.plano_atual || "",
    ]);

    const csvContent = [
      headers.join(";"),
      ...csvData.map((row) => row.join(";")),
    ].join("\n");

    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "faturas.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "10px",
          alignItems: "center",
        }}
      >
        <h3>Faturas</h3>
        <div>
          <IconButton
            onClick={handleRefresh}
            title="Atualizar dados"
            style={{ marginRight: "8px" }}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={exportToCSV}
            size="small"
          >
            Exportar CSV
          </Button>
        </div>
      </div>

      <div
        style={{
          marginBottom: "15px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div>
          {state.selectedRows.length > 0 && (
            <div>
              <Button
                variant="contained"
                color="secondary"
                style={{ marginRight: "10px" }}
                onClick={() => handleSelectedActions("cancel")}
              >
                Cancelar faturas ({state.selectedRows.length})
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSelectedActions("include")}
              >
                Incluir no plano atual
              </Button>
            </div>
          )}
        </div>
        <div>
          <input
            type="text"
            placeholder="Buscar por descrição, status (pago, pendente, expirado) ou ID..."
            value={state.searchTerm}
            onChange={handleSearch}
            style={{
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              width: "300px",
            }}
          />
        </div>
      </div>

      <div
        style={{
          overflowX: "auto",
          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          borderRadius: "4px",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1px solid #ddd",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  border: "1px solid #ddd",
                  padding: "10px",
                  backgroundColor: "#f2f2f2",
                }}
              >
                <input
                  type="checkbox"
                  onChange={selectAllRows}
                  checked={allSelected}
                  ref={(input) => {
                    if (input) {
                      input.indeterminate = someSelected;
                    }
                  }}
                />
              </th>
              {columns.map((column) => (
                <th
                  key={column.id}
                  style={{
                    border: "1px solid #ddd",
                    padding: "10px",
                    backgroundColor: "#f2f2f2",
                    cursor: column.sortable ? "pointer" : "default",
                  }}
                  onClick={() =>
                    column.sortable && handleRequestSort(column.id)
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {column.label}
                    {column.sortable && (
                      <span
                        style={{ display: "inline-block", marginLeft: "5px" }}
                      >
                        {state.orderBy === column.id ? (
                          state.order === "asc" ? (
                            <ArrowUpwardIcon fontSize="small" />
                          ) : (
                            <ArrowDownwardIcon fontSize="small" />
                          )
                        ) : null}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedPayments.map((payment, index) => {
              const realIndex = sortedPayments.indexOf(payment);
              const isItemSelected = isSelected(realIndex);

              return (
                <tr
                  key={index}
                  style={{
                    backgroundColor: isItemSelected
                      ? "#e3f2fd"
                      : index % 2 === 0
                      ? "#f9f9f9"
                      : "white",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onMouseOver={(e) => {
                    if (!isItemSelected)
                      e.currentTarget.style.backgroundColor = "#f0f0f0";
                  }}
                  onMouseOut={(e) => {
                    if (!isItemSelected)
                      e.currentTarget.style.backgroundColor =
                        index % 2 === 0 ? "#f9f9f9" : "white";
                  }}
                >
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: "10px",
                      textAlign: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isItemSelected}
                      onChange={() => toggleRowSelection(realIndex)}
                    />
                  </td>
                  <td
                    style={{ border: "1px solid #ddd", padding: "10px" }}
                    onClick={() => payment.id && openFaturaDetails(payment)}
                  >
                    {payment.id}
                  </td>
                  <td
                    style={{ border: "1px solid #ddd", padding: "10px" }}
                    onClick={() => payment.id && openFaturaDetails(payment)}
                  >
                    {payment.vencimento &&
                      new Date(payment.vencimento + 'T00:00:00-03:00').toLocaleDateString("pt-BR")}
                  </td>
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: "10px",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => payment.id && openFaturaDetails(payment)}
                  >
                    {payment.valor}
                  </td>
                  <td
                    style={{ border: "1px solid #ddd", padding: "10px" }}
                    onClick={() => payment.id && openFaturaDetails(payment)}
                  >
                    <div style={getStatusStyle(payment.status)}>
                      {translateStatus(payment.status)}
                    </div>
                  </td>
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: "10px",
                      color: "red",
                      fontWeight: payment.atraso ? "bold" : "normal",
                    }}
                    onClick={() => payment.id && openFaturaDetails(payment)}
                  >
                    {payment.atraso}
                  </td>
                  <td
                    style={{ border: "1px solid #ddd", padding: "10px" }}
                    onClick={() => payment.id && openFaturaDetails(payment)}
                  >
                    {payment.descrição}
                  </td>
                  <td
                    style={{ border: "1px solid #ddd", padding: "10px" }}
                    onClick={() => payment.id && openFaturaDetails(payment)}
                  >
                    {payment.data_pagamento &&
                      new Date(payment.data_pagamento + 'T00:00:00-03:00').toLocaleDateString(
                        "pt-BR"
                      )}
                  </td>
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: "10px",
                      cursor: "pointer",
                      color: payment.link ? "blue" : "inherit",
                      textDecoration: payment.link ? "underline" : "none",
                    }}
                    onClick={() =>
                      payment.link && window.open(payment.link, "_blank")
                    }
                  >
                    {payment.link ? "Visualizar" : ""}
                  </td>
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: "10px",
                      fontWeight: payment.plano_atual ? "bold" : "normal",
                    }}
                    onClick={() => payment.id && openFaturaDetails(payment)}
                  >
                    {payment.plano_atual}
                  </td>
                </tr>
              );
            })}
            {emptyRows > 0 && (
              <tr style={{ height: 53 * emptyRows }}>
                <td
                  colSpan={columns.length + 1}
                  style={{ border: "1px solid #ddd" }}
                />
              </tr>
            )}
            {filteredPayments.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    border: "1px solid #ddd",
                  }}
                >
                  Nenhuma fatura encontrada
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "20px",
        }}
      >
        <div>
          <span>Linhas por página: </span>
          <select
            value={state.rowsPerPage}
            onChange={handleChangeRowsPerPage}
            style={{ marginLeft: "5px", padding: "5px" }}
          >
            {[5, 10, 25, 50, 100].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div>
          <span>
            {filteredPayments.length > 0
              ? `Mostrando ${start + 1}-${Math.min(
                  start + state.rowsPerPage,
                  filteredPayments.length
                )}`
              : "Nenhum item"}
          </span>
        </div>
        <div>
          <button
            type="button"
            onClick={(e) => handleChangePage(state.currentPage - 1, e)}
            disabled={state.currentPage === 0}
            style={{
              padding: "5px 10px",
              margin: "0 5px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              backgroundColor: state.currentPage === 0 ? "#f0f0f0" : "white",
              cursor: state.currentPage === 0 ? "not-allowed" : "pointer",
            }}
          >
            Anterior
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => handleChangePage(i, e)}
              disabled={i === state.currentPage}
              style={{
                padding: "5px 10px",
                margin: "0 5px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                backgroundColor: i === state.currentPage ? "#007bff" : "white",
                color: i === state.currentPage ? "white" : "black",
                cursor: i === state.currentPage ? "default" : "pointer",
              }}
            >
              {i + 1}
            </button>
          )).slice(
            Math.max(0, state.currentPage - 2),
            Math.min(totalPages, state.currentPage + 3)
          )}
          <button
            type="button"
            onClick={(e) => handleChangePage(state.currentPage + 1, e)}
            disabled={state.currentPage >= totalPages - 1}
            style={{
              padding: "5px 10px",
              margin: "0 5px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              backgroundColor:
                state.currentPage >= totalPages - 1 ? "#f0f0f0" : "white",
              cursor:
                state.currentPage >= totalPages - 1 ? "not-allowed" : "pointer",
            }}
          >
            Próximo
          </button>
        </div>
      </div>

      {/* Modal de opções da fatura */}
      <Dialog
        open={state.open}
        onClose={() => setState({ ...state, open: false })}
      >
        <div
          style={{ padding: "20px", textAlign: "center", minWidth: "300px" }}
        >
          <IconButton
            onClick={() => setState({ ...state, open: false })}
            style={{ float: "right" }}
          >
            <CloseIcon />
          </IconButton>
          <DialogTitle>Opções da fatura</DialogTitle>

          {state.selectedFatura && (
            <div style={{ marginBottom: "20px", textAlign: "left" }}>
              <p>
                <strong>ID:</strong> {state.selectedFatura.id}
              </p>
              <p>
                <strong>Vencimento:</strong>{" "}
                {state.selectedFatura.vencimento &&
                  new Date(state.selectedFatura.vencimento + 'T00:00:00-03:00').toLocaleDateString(
                    "pt-BR"
                  )}
              </p>
              <p>
                <strong>Valor:</strong> {state.selectedFatura.valor}
              </p>
              <p>
                <strong>Status:</strong>{" "}
                {translateStatus(state.selectedFatura.status)}
              </p>
              <p>
                <strong>Descrição:</strong> {state.selectedFatura.descrição}
              </p>
            </div>
          )}

          <Button
            variant="contained"
            color="secondary"
            style={{ margin: "10px" }}
            onClick={async () => {
              if (state.selectedFatura) {
                await props.hidePayment(state.selectedFatura);
                setState({ ...state, open: false });
                refetch();
              }
            }}
          >
            Cancelar fatura
          </Button>
          <Button
            variant="contained"
            color="primary"
            style={{ margin: "10px" }}
            onClick={async () => {
              if (state.selectedFatura) {
                await props.setPlan(state.selectedFatura, props.payment_plan);
                setState({ ...state, open: false });
                refetch();
              }
            }}
          >
            Incluir no plano atual
          </Button>
          {state.selectedFatura && state.selectedFatura.link && (
            <Button
              variant="outlined"
              color="primary"
              style={{ margin: "10px" }}
              onClick={() => window.open(state.selectedFatura.link, "_blank")}
            >
              Visualizar boleto
            </Button>
          )}
        </div>
      </Dialog>
    </>
  );
});

export default NativeInvoiceTable;
